import React, { useState, useEffect, useRef } from 'react';
import * as CountryFlags from 'country-flag-icons/react/3x2';
import { ALPHA3_TO_ALPHA2 } from '../../utils/CountryFlag';
import ThemedSelect from '../../utils/ThemedSelect';
import { AdminTopNav, AdminSideNav } from '../administrationNavigation/AdminConsoleNavigation';
import GetCookie from '../../utils/GetCookie'
import ErrorPopup from '../../utils/popup/ErrorPopup';
import WarningPopup from '../../utils/popup/WarningPopup';
import SuccessPopup from '../../utils/popup/SuccessPopup';
import ProcessingPopup from '../../utils/popup/ProcessingPopup';
import DeleteConfirmationModal from '../../utils/DeleteConfirmationModal';
import TransferOwnershipModal from './TransferOwnershipModal';
import TrimContent from '../../utils/TrimContent';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import PaginationControls from '../../utils/PaginationControls';
import { useTimezone } from '../../utils/TimezoneContext';

// Mirrors the PlatformRole enum in the backend.
const PLATFORM_ROLES = [
  { value: 'ADMIN', label: 'Admin' },
  { value: 'GLOBAL_VIEWER', label: 'Global Viewer' },
  { value: 'COUNTRY_VIEWER', label: 'Country Viewer' },
  { value: 'SUPER_USER', label: 'Super User' },
  { value: 'STANDARD_USER', label: 'Standard User' }
];

// Which country controls a role gets. An Admin sits above country entirely. A
// Global Viewer already sees every country, so its list is shown ticked and
// read-only and no countries are sent. Country Viewer, Super User and Standard
// User must pick at least one — for a Country Viewer that list is its actual
// read scope, not just an affiliation. A Standard User cannot hold a headship.
// The backend re-checks all of this, so a slip here shows up as an error message
// rather than as extra access.
// country-flag-icons keys on ISO-2 while countries.iso_code is ISO-3, hence the
// ALPHA3_TO_ALPHA2 bridge. A market without an entry simply renders no flag
// rather than breaking the card.

const showsCountries = (role) => role !== 'ADMIN';
const seesAllCountries = (role) => role === 'GLOBAL_VIEWER';
const requiresCountry = (role) => role === 'SUPER_USER' || role === 'STANDARD_USER' || role === 'COUNTRY_VIEWER';
const canHeadCountry = (role) => role === 'GLOBAL_VIEWER' || role === 'SUPER_USER' || role === 'COUNTRY_VIEWER';

const AdminIamUserManagement = () => {
  const navigate = useNavigate();
  const accessToken = GetCookie('access_token');
  const { formatDateTime } = useTimezone();

  // State variables for IAM users listing
  const [iamUsers, setIamUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // State variables for form management
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('STANDARD_USER');
  const [availableCountries, setAvailableCountries] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [headCountries, setHeadCountries] = useState([]);
  // Extra permission sets assigned on top of the role (e.g. Developer). The dropdown
  // API only returns internal sets to admins allowed to see them.
  const [availablePermissionSets, setAvailablePermissionSets] = useState([]);
  const [selectedPermissionSets, setSelectedPermissionSets] = useState([]);
  
  // Delete state - updated for reusable modal
  const [deleteUser, setDeleteUser] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // What the user about to be deleted still owns, and who may take it over.
  // The delete endpoint refuses while anything is still held, so this drives
  // the modal's transfer-first gate.
  const [deleteOwnership, setDeleteOwnership] = useState(null);
  const [loadingDeleteOwnership, setLoadingDeleteOwnership] = useState(false);

  // Transfer ownership state
  const [transferUser, setTransferUser] = useState(null);
  const [showTransferModal, setShowTransferModal] = useState(false);

  // Popup states
  const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
  const [popupErrorMessage, setPopupErrorMessage] = useState('');
  const [popupWarningMessage, setPopupWarningMessage] = useState('');
  const [isSuccessPopupVisible, setIsSuccessPopupVisible] = useState(false);
  const [popupSuccessMessage, setPopupSuccessMessage] = useState('');

  // API base URL
  const API_BASE_URL = '/pceapi/administration';

  // HTTP headers with authorization
  const headers = {
    Authorization: accessToken
  };

  // Fetch IAM users on component mount and when dependencies change
  useEffect(() => {
    fetchIamUsers();
  }, [page, size, search, refreshTrigger]);

  // Initialize searchInput with search value
  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  // Fetch available countries when form is shown
  useEffect(() => {
    if (showForm) {
      fetchAvailableCountries();
      fetchAvailablePermissionSets();
    }
  }, [showForm]);

  // Load initial data if editing
  useEffect(() => {
    if (editingUser) {
      setEmail(editingUser.email || '');
      setName(editingUser.name || '');
      setRole(editingUser.role || 'STANDARD_USER');
      setSelectedCountries(editingUser.country_ids || []);
      setHeadCountries(editingUser.head_of_country_ids || []);
    } else {
      setEmail('');
      setName('');
      setRole('STANDARD_USER');
      setSelectedCountries([]);
      setHeadCountries([]);
    }
  }, [editingUser]);

  // Fetch IAM users with pagination and search
  const fetchIamUsers = async () => {
    setIsProcessingPopupVisible(true);
    setProcessingMessage('Loading IAM users');
    
    try {
      const response = await axios.post(
        `${API_BASE_URL}/iam-users/list`, 
        {
          page,
          size,
          search: search || undefined
        },
        { headers }
      );
      
      const data = response.data;
      
      if (data.status === 'NotAuthorized') {
        setIsProcessingPopupVisible(false);
        navigate('/admin-login');
        return;
      }
      
      if (data.status === 'failed') {
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(data.message || 'Failed to load IAM users');
        setIsErrorPopupVisible(true);
        return;
      }
      
      setIamUsers(data.data || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Error fetching IAM users:', error);
      setPopupErrorMessage(error.message || 'Failed to load IAM users');
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
    }
  };

  const fetchOwnershipSummary = async (userId) => {
    setLoadingDeleteOwnership(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/iam-users/${userId}/ownership-summary`,
        { headers }
      );
      const data = response.data;
      if (data.status === 'success') {
        setDeleteOwnership(data.data);
        return data.data;
      }
      setPopupErrorMessage(data.message || 'Failed to load ownership details');
      setIsErrorPopupVisible(true);
      return null;
    } catch (error) {
      console.error('Error fetching ownership summary:', error);
      setPopupErrorMessage(error.message || 'Failed to load ownership details');
      setIsErrorPopupVisible(true);
      return null;
    } finally {
      setLoadingDeleteOwnership(false);
    }
  };

  const handleTransferBeforeDelete = async (targetUserId) => {
    if (!deleteUser) return false;

    setIsProcessingPopupVisible(true);
    setProcessingMessage('Transferring ownership');
    try {
      const response = await axios.post(
        `${API_BASE_URL}/iam-users/transfer-ownership`,
        {
          source_user_id: deleteUser.user_id,
          target_user_id: targetUserId,
          transfer_type: 'COMPLETE'
        },
        { headers }
      );
      const result = response.data;

      if (result.status !== 'success') {
        setPopupErrorMessage(result.message || 'Failed to transfer ownership');
        setIsErrorPopupVisible(true);
        return false;
      }

      const refreshed = await fetchOwnershipSummary(deleteUser.user_id);
      if (!refreshed || refreshed.owns_anything) {
        setPopupErrorMessage(
          'Ownership was transferred, but this user still holds something. ' +
          'Review the remaining items and transfer them before deleting.'
        );
        setIsErrorPopupVisible(true);
        return false;
      }
      return true;
    } catch (error) {
      console.error('Transfer before delete error:', error);
      setPopupErrorMessage(error.message || 'Failed to transfer ownership');
      setIsErrorPopupVisible(true);
      return false;
    } finally {
      setIsProcessingPopupVisible(false);
    }
  };

  // Fetch active countries for the country controls
  const fetchAvailableCountries = async () => {
    setIsProcessingPopupVisible(true);
    setProcessingMessage('Loading available countries');

    try {
      const response = await axios.get(
        `${API_BASE_URL}/iam-users/get-countries`,
        { headers }
      );
      const data = response.data;

      if (data.status === 'NotAuthorized') {
        setIsProcessingPopupVisible(false);
        navigate('/admin-login');
        return;
      }

      if (data.status === 'failed') {
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(data.message || 'Failed to load available countries');
        setIsErrorPopupVisible(true);
        return;
      }

      setAvailableCountries(data.data || []);
    } catch (error) {
      console.error('Error fetching countries:', error);
      setPopupErrorMessage(error.message || 'Failed to load available countries');
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
    }
  };

  // Assignable permission sets for the form. Failure is non-fatal: the form still
  // works, it just can't change the user's extra sets.
  const fetchAvailablePermissionSets = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/iam-users/get-permission-sets`,
        { headers }
      );
      const data = response.data;
      if (data.status === 'NotAuthorized') {
        navigate('/admin-login');
        return;
      }
      setAvailablePermissionSets(data.status === 'success' ? (data.data || []) : []);
    } catch (error) {
      console.error('Error fetching permission sets:', error);
      setAvailablePermissionSets([]);
    }
  };

  const applyUserToForm = (user) => {
    setEmail(user?.email || '');
    setName(user?.name || '');
    setRole(user?.role || 'STANDARD_USER');
    setSelectedCountries(user?.country_ids || []);
    setHeadCountries(user?.head_of_country_ids || []);
    // All of the user's sets, including any internal one this admin can't see:
    // they are sent back unchanged so saving doesn't drop them.
    setSelectedPermissionSets(user?.permission_set_ids || []);
  };

  const togglePermissionSet = (id) => {
    setSelectedPermissionSets(prev =>
      prev.includes(id) ? prev.filter(setId => setId !== id) : [...prev, id]
    );
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingUser(null);
    applyUserToForm(null);
  };

  // Form submission handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!email.trim()) {
      setPopupErrorMessage('Email is required');
      setIsErrorPopupVisible(true);
      return;
    }
  
    if (!name.trim()) {
      setPopupErrorMessage('Name is required');
      setIsErrorPopupVisible(true);
      return;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setPopupErrorMessage('Please enter a valid email address');
      setIsErrorPopupVisible(true);
      return;
    }

    if (requiresCountry(role) && selectedCountries.length === 0) {
      setPopupErrorMessage('Please select at least one country for this role');
      setIsErrorPopupVisible(true);
      return;
    }

    setIsProcessingPopupVisible(true);
    setProcessingMessage(editingUser ? 'Updating IAM user' : 'Creating IAM user');
    
    try {
      // A Global Viewer's countries are derived from its role, so nothing is sent
      // for it — the ticked, read-only list above is display only.
      const formData = {
        email: email.trim(),
        name: name.trim(),
        role: role,
        country_ids: seesAllCountries(role) ? [] : selectedCountries,
        head_of_country_ids: headCountries,
        permission_set_ids: selectedPermissionSets
      };
      
      let response;
      if (editingUser) {
        response = await axios.post(
          `${API_BASE_URL}/iam-users/update/${editingUser.user_id}`, 
          formData,
          { headers }
        );
      } else {
        response = await axios.post(
          `${API_BASE_URL}/iam-users/create`, 
          formData,
          { headers }
        );
      }
      
      const result = response.data;
      
      if (result.status === 'NotAuthorized') {
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(result.message);
        setIsErrorPopupVisible(true);
        return;
      }
      
      if (result.status === 'failed') {
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(result.message || (editingUser ? 'Failed to update IAM user' : 'Failed to create IAM user'));
        setIsErrorPopupVisible(true);
        return;
      }
      
      setPopupSuccessMessage(result.message || (editingUser ? 'IAM user updated successfully' : 'IAM user created successfully'));
      setIsSuccessPopupVisible(true);
      setRefreshTrigger(prev => prev + 1);
      handleCloseForm();
    } catch (error) {
      console.error('Submit error:', error);
      setPopupErrorMessage(error.message || (editingUser ? 'Failed to update IAM user' : 'Failed to create IAM user'));
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
    }
  };
  
  // Delete handler - updated for reusable modal
  const handleDelete = async () => {
    if (!deleteUser) return;
    
    setIsProcessingPopupVisible(true);
    setProcessingMessage('Deleting IAM user');
    setShowDeleteConfirm(false);
    
    try {
      const response = await axios.post(
        `${API_BASE_URL}/iam-users/delete/${deleteUser.user_id}`,
        {},
        { headers }
      );
      
      const result = response.data;
      
      if (result.status === 'NotAuthorized') {
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(result.message);
        setIsErrorPopupVisible(true);
        return;
      }
      
      if (result.status === 'failed') {
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(result.message || 'Failed to delete IAM user');
        setIsErrorPopupVisible(true);
        return;
      }
      
      setPopupSuccessMessage(result.message || 'IAM user deleted successfully');
      setIsSuccessPopupVisible(true);
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Delete error:', error);
      setPopupErrorMessage(error.message || 'Failed to delete IAM user');
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
      setDeleteUser(null);
    }
  };
  
  // Switching role changes which country controls apply, so drop anything the new
  // role cannot hold — a stale selection would otherwise be submitted and rejected.
  const handleRoleChange = (nextRole) => {
    setRole(nextRole);
    if (!showsCountries(nextRole)) {
      setSelectedCountries([]);
      setHeadCountries([]);
    } else if (!canHeadCountry(nextRole)) {
      setHeadCountries([]);
    } else if (!seesAllCountries(nextRole)) {
      setHeadCountries(prev => prev.filter(id => selectedCountries.includes(id)));
    }
  };

  // Toggle country affiliation
  const toggleCountry = (id) => {
    if (selectedCountries.includes(id)) {
      setSelectedCountries(prev => prev.filter(countryId => countryId !== id));
      // Un-ticking a country drops its headship too — a Super User heads only
      // countries it is actually assigned to.
      setHeadCountries(prev => prev.filter(countryId => countryId !== id));
    } else {
      setSelectedCountries(prev => [...prev, id]);
    }
  };

  // Toggle country headship
  const toggleHeadCountry = (id) => {
    if (headCountries.includes(id)) {
      setHeadCountries(prev => prev.filter(countryId => countryId !== id));
    } else {
      setHeadCountries(prev => [...prev, id]);
    }
  };

  const handleOpenEditForm = (user) => {
    applyUserToForm(user);
    setEditingUser(user);
    setShowForm(true);
  };

  // Open delete confirmation dialog - updated for reusable modal
  const handleOpenDeleteConfirm = (user) => {
    setDeleteUser(user);
    setShowDeleteConfirm(true);
    setDeleteOwnership(null);
    fetchOwnershipSummary(user.user_id);
  };

  // Open transfer ownership modal
  const handleOpenTransferModal = (user) => {
    setTransferUser(user);
    setShowTransferModal(true);
  };

  // Popup close handlers
  const closeErrorPopup = () => {
    setIsErrorPopupVisible(false);
  };

  const closeSuccessPopup = () => {
    setIsSuccessPopupVisible(false);
  };

  // Helper to show a role's display label
  const roleLabel = (value) => {
    const match = PLATFORM_ROLES.find(r => r.value === value);
    return match ? match.label : (value || 'None');
  };

  // Handle search input change
  const handleSearchInputChange = (e) => {
    setSearchInput(e.target.value);
  };
  
  // Handle search button click
  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };
  
  // Handle Enter key press in search input
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div className="main-window">
      <AdminTopNav />
      <div className="container-fluid">
        <div className="row">
          <AdminSideNav />
          <main className="col-12 col-md-9 col-lg-10 ms-sm-auto console-main-window">
            <div className="mx-5 pb-5">
              <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center mb-0 margin-top-4">
                <p className="ev-med-p-blue mb-1">IAM Users <span className="ev-light-reg">({total})</span></p>
                {!showForm && ( 
                  <button 
                    className="ev-btn-yellow-fill-round ev-btn-med ev-bold-reg" 
                    onClick={() => {
                      setEditingUser(null);
                      applyUserToForm(null);
                      setShowForm(true);
                    }}
                  >
                    <i className="bi bi-plus-circle me-1"></i> Create IAM User
                  </button>
                )}
              </div>
              <p className="ev-message-card2 mb-4">Manage IAM users, their platform role and their countries.</p>

              {/* Show form if in edit/create mode, otherwise show list */}
              {showForm ? (
                <div className="ev-card-2 w-100 ev-padding-xlarge">
                  <p className="ev-med-p-blue mb-4">
                    {editingUser ? 'Edit User' : 'Create User'}
                  </p>
                  
                  <form onSubmit={handleSubmit}>
                    <div className="row">
                      <div className="col-5">
                        <div className="form-group mb-3">
                          <label className="ev-reg-p-dark mb-1">Email <span className="text-danger">*</span></label>
                          {editingUser ? (
                              <input type="email" className="formField3"  value={email} onChange={(e) => setEmail(e.target.value)} disabled  required/>
                            ) : (
                              <input type="email"  className="formField3" value={email} onChange={(e) => setEmail(e.target.value)}required/>
                            )}
                        </div>
                        
                        <div className="form-group mb-4">
                          <label className="ev-reg-p-dark mb-1">Name <span className="text-danger">*</span></label>
                          <input type="text" className="formField3" value={name} onChange={(e) => setName(e.target.value)} required/>
                        </div>

                        <div className="form-group mb-4">
                          <label className="ev-reg-p-dark mb-1">Role <span className="text-danger">*</span></label>
                          <ThemedSelect value={role} options={PLATFORM_ROLES} onChange={handleRoleChange}
                            ariaLabel="Platform role" />
                        </div>

                        {availablePermissionSets.length > 0 && (
                          <div className="form-group mb-4">
                            <label className="ev-reg-p-dark mb-1">Additional Permission Sets</label>
                            <p className="ev-small-p-dark mb-2">Granted on top of the role.</p>
                            {/* Compact card, as wide as its content: name + checkbox per set */}
                            <div className="ev-card-3 d-inline-flex flex-column px-3 py-2">
                              {availablePermissionSets.map((ps, i) => (
                                <div
                                  key={ps.id}
                                  className={`d-flex align-items-center justify-content-between gap-3 py-1 ${i > 0 ? 'border-top' : ''}`}
                                >
                                  <label className="ev-reg-p-dark mb-0 text-nowrap" htmlFor={`perm-set-${ps.id}`}>
                                    <i className="bi bi-shield-lock me-1"></i>{ps.name}
                                    {ps.is_active === false && <span className="ev-small-p-dark ms-1">(inactive)</span>}
                                  </label>
                                  <input
                                    className="form-check-input m-0"
                                    type="checkbox"
                                    id={`perm-set-${ps.id}`}
                                    checked={selectedPermissionSets.includes(ps.id)}
                                    onChange={() => togglePermissionSet(ps.id)}
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {!editingUser && (
                          <div className="alert alert-info">
                            <i className="bi bi-info-circle me-1"></i><b>Autogenerated Password</b><br />
                            Upon user creation, a temporary password will be automatically sent to the user's email address.
                          </div>
                        )}
                        
                        <div className="mt-4 text-end">
                          <button type="submit" className="ev-btn-blue-fill-round ev-bold-reg" >
                          <i className="bi bi-person"></i> {editingUser ? 'Update User' : 'Create User'}
                          </button>
                          <span>&nbsp;</span>
                          <button type="button" className="ev-btn-blue-outline-round ev-bold-reg ms-2" onClick={handleCloseForm}>Cancel</button>
                        </div>
                      </div>
                      
                      <div className="col-7">
                        {!showsCountries(role) ? (
                          <div className="alert alert-info">
                            <i className="bi bi-info-circle me-1"></i><b>Every country</b><br />
                            An Admin has access across all countries, so no country selection is needed.
                          </div>
                        ) : (
                          <>
                            <label className="ev-reg-p-dark mb-1">
                              Countries {requiresCountry(role) && <span className="text-danger">*</span>}
                            </label>
                            <div className="ev-card-3 country-grid">
                              {availableCountries.length === 0 ? (
                                <p className="text-muted ev-small-p-dark">
                                  {isProcessingPopupVisible ? 'Loading countries' : 'No countries available'}
                                </p>
                              ) : (
                                availableCountries.map(country => {
                                  const isSelected = seesAllCountries(role) || selectedCountries.includes(country.id);
                                  const isHead = headCountries.includes(country.id);
                                  return (
                                    <div
                                      key={country.id}
                                      className={`country-card ${isSelected ? 'is-selected' : ''} ${seesAllCountries(role) ? 'is-locked' : ''}`}
                                    >
                                      <label className="country-card__main" htmlFor={`country-${country.id}`}>
                                        <input
                                          className="form-check-input country-card__check"
                                          type="checkbox"
                                          id={`country-${country.id}`}
                                          checked={isSelected}
                                          disabled={seesAllCountries(role)}
                                          onChange={() => toggleCountry(country.id)}
                                        />
                                        <span className="country-card__flag" aria-hidden="true">
                                          {CountryFlags[ALPHA3_TO_ALPHA2[country.iso_code]]
                                            ? React.createElement(CountryFlags[ALPHA3_TO_ALPHA2[country.iso_code]])
                                            : null}
                                        </span>
                                        <span className="country-card__text">
                                          <span className="country-card__name" title={country.name}>{country.name}</span>
                                          <span className="country-card__code">{country.iso_code}</span>
                                        </span>
                                      </label>
                                      {canHeadCountry(role) && isSelected && (
                                        <button
                                          type="button"
                                          className={`country-card__head ${isHead ? 'is-head' : ''}`}
                                          aria-pressed={isHead}
                                          title={isHead ? 'Remove headship' : 'Make Country Head'}
                                          onClick={() => toggleHeadCountry(country.id)}
                                        >
                                          <i className={`bi ${isHead ? 'bi-star-fill' : 'bi-star'}`}></i>
                                          {isHead ? 'Country Head' : 'Make head'}
                                        </button>
                                      )}
                                    </div>
                                  );
                                })
                              )}
                            </div>
                            <div className="mt-3">
                              <p className="ev-small-p-dark mb-0">
                                {seesAllCountries(role)
                                  ? `A Global Viewer sees all ${availableCountries.length} countries`
                                  : `Selected: ${selectedCountries.length} countries`}
                                {canHeadCountry(role) && ` · Country Head of ${headCountries.length}`}
                              </p>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </form>
                </div>
              ) : (
                /* IAM Users List */
                <div className="ev-card-4 w-100 ev-padding-large">
                  <div className="row mt-1 mb-0">
                    <div className="col-6">
                      <div className="position-relative">
                        <input
                          type="text"
                          className="form-control search-input ms-0"
                          placeholder="Search by email or name"
                          value={searchInput}
                          onChange={handleSearchInputChange}
                          onKeyPress={handleSearchKeyPress}
                        />
                        <button 
                          className="ev-btn-lightblue-fill-round search-btn-inside"
                          style={{ left: 0 }}
                          onClick={handleSearch}
                        >
                          <i className="bi bi-search"></i>
                        </button>
                      </div>
                    </div>
                    
                    <div className="col-6 text-end">
                      <button 
                        className="ev-btn-lightblue-fill-round ev-bold-reg ms-2" 
                        onClick={() => setRefreshTrigger(prev => prev + 1)}
                      ><i className="bi bi-arrow-repeat"></i> Refresh 
                      </button>
                    </div>
                  </div>

                  <div className="table-container">
                    <table className="table-custom1 mt-0 overflow-hidden w-100">
                      <thead>
                        <tr>
                          <th className="w-25">
                            <label>User</label>
                          </th>
                          <th className="w-25">
                            <label>Role</label>
                          </th>
                          <th className="w-25">
                            <label>Created By</label>
                          </th>
                          <th className="w-15">
                            <label>Created At</label>
                          </th>
                          <th className="w-10">
                            <label>Actions</label>
                          </th>
                        </tr>
                      </thead>
                    </table>

                    <div className="scrollable-element dashboard-right-contentarea-table-height-1">
                      <table className="table-custom1 overflow-hidden w-100">
                        <tbody>
                          {iamUsers.length === 0 ? (
                            <div className="w-100 text-center">
                              <p className="ev-small-p-blue mt-5">
                                {isProcessingPopupVisible ? 'Loading Users' : 'No IAM users found'}
                              </p>
                            </div>
                          ) : (
                            iamUsers.map((user) => (
                              <tr key={user.user_id}>
                                <td className="align-top w-25">
                                  <label className="ev-small-p-dark">&nbsp;{user.name}</label><br />
                                  <label>
                                    <button 
                                        className="ev-small-p-blue bg-transparent border-0"  
                                        onClick={() => handleOpenEditForm(user)}
                                      >
                                        <TrimContent text={user.email} maxLength={25} />
                                    </button>
                                  </label>
                                </td>
                                <td className="align-top w-25">
                                  <span className="ev-message-card1 me-1 mb-1">
                                    {roleLabel(user.role)}
                                  </span>
                                  {user.head_of_country_ids && user.head_of_country_ids.length > 0 && (
                                    <span className="ev-message-card1 me-1 mb-1">
                                      Country Head &times;{user.head_of_country_ids.length}
                                    </span>
                                  )}
                                </td>
                                <td className="align-top w-25 avatar-tooltip" data-tooltip={user.creator_email || 'None'}>
                                  <div className="ev-message-card5 ev-fs-11">
                                    <TrimContent text={user.creator_email} maxLength={25} />
                                  </div>
                                </td>
                                <td className="align-top w-15">{formatDateTime(user.created_at)}</td>
                                <td className="align-top w-10">
                                  <div className="btn-group" role="group">
                                    <button
                                      type="button"
                                      className="btn ev-btn-skyblue-fill-round ev-radius-xsmall ev-btn-med pe-2 ps-2 avatar-tooltip"
                                      // style={{ backgroundColor: '#fff3cd', color: '#856404' }}
                                      onClick={() => handleOpenEditForm(user)}
                                      data-tooltip="Edit User"
                                    >
                                      <i className="bi bi-pencil"></i>
                                    </button>
                                    <button
                                      type="button"
                                      className="btn ev-btn-blue-fill-round ev-radius-xsmall ev-btn-med pe-2 ps-2 avatar-tooltip"
                                      onClick={() => handleOpenTransferModal(user)}
                                      data-tooltip="Transfer Ownership"
                                    >
                                      <i className="bi bi-arrow-left-right"></i>
                                    </button>
                                    <button
                                      type="button"
                                      className="btn ev-btn-red-fill-round ev-radius-xsmall ev-btn-med pe-2 ps-2 avatar-tooltip"
                                      onClick={() => handleOpenDeleteConfirm(user)}
                                      data-tooltip="Delete"
                                    >
                                      <i className="bi bi-trash"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Pagination */}
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <div className="ev-small-p-dark">
                      Showing {iamUsers.length} of {total} entries
                    </div>
                    <div className="d-flex gap-2">
                      <button 
                        className="ev-btn-light-fill" 
                        disabled={page === 1}
                        onClick={() => setPage(page - 1)}
                      >
                        <i className="bi bi-chevron-left"></i> 
                      </button>
                      <button className="ev-btn-light-fill" disabled>
                        Page {page} of {Math.ceil(total / size) || 1}
                      </button>
                      <button 
                        className="ev-btn-light-fill" 
                        disabled={page >= Math.ceil(total / size)}
                        onClick={() => setPage(page + 1)}
                      >
                        <i className="bi bi-chevron-right"></i>
                      </button>
                    </div>
                    <div>
                      <PaginationControls 
                        initialSize={size}
                        onSizeChange={(newSize) => {
                          setSize(newSize);
                          setPage(1);
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Reusable Delete Confirmation Modal */}
              <DeleteConfirmationModal
                show={showDeleteConfirm}
                onClose={() => {
                  setShowDeleteConfirm(false);
                  setDeleteUser(null);
                  setDeleteOwnership(null);
                }}
                onConfirm={handleDelete}
                objectName="user"
                itemName={deleteUser ? `${deleteUser.name} (${deleteUser.email})` : ''}
                bodyContent={
                  <p className="text-danger mt-2">
                    <i className="bi bi-exclamation-triangle me-1"></i>
                    This action cannot be undone and will remove all associated permissions.
                  </p>
                }
                requireOwnerTransfer
                ownerOptions={(deleteOwnership?.eligible_targets || [])
                  .filter(u => u.can_receive_complete)
                  .map(u => ({
                    id: u.user_id,
                    name: u.name,
                    email: u.email,
                    role: roleLabel(u.role)
                  }))}
                ownerOptionsLoading={loadingDeleteOwnership}
                ownershipCounts={deleteOwnership?.counts || null}
                ownsAnything={deleteOwnership ? deleteOwnership.owns_anything : true}
                onTransferOwnership={handleTransferBeforeDelete}
              />

              {/* Transfer Ownership Modal */}
              <TransferOwnershipModal
                show={showTransferModal}
                onClose={() => {
                  setShowTransferModal(false);
                  setTransferUser(null);
                }}
                sourceUser={transferUser}
                onTransferred={(message) => {
                  setPopupSuccessMessage(message || 'Ownership transferred successfully');
                  setIsSuccessPopupVisible(true);
                  setRefreshTrigger(prev => prev + 1);
                }}
                onError={(message) => {
                  setPopupErrorMessage(message || 'Failed to transfer ownership');
                  setIsErrorPopupVisible(true);
                }}
                onWarning={(message) => setPopupWarningMessage(message)}
                onProcessing={(message) => {
                  setProcessingMessage(message || '');
                  setIsProcessingPopupVisible(Boolean(message));
                }}
              />
            </div>
          </main>
        </div>
      </div>

      {/* Popups */}
      {isProcessingPopupVisible && (
        <ProcessingPopup message={processingMessage} />
      )}
      
      {popupWarningMessage && (
        <WarningPopup
          title="Selection changed"
          message={popupWarningMessage}
          onClose={() => setPopupWarningMessage('')}
        />
      )}
      {isErrorPopupVisible && (
        <ErrorPopup  
          onClose={closeErrorPopup}
          message={popupErrorMessage}
        />
      )}
      
      {isSuccessPopupVisible && (
        <SuccessPopup 
          onClose={closeSuccessPopup}
          message={popupSuccessMessage}
        />
      )}
    </div>
  );
};

export default AdminIamUserManagement;